'use client';
import { useProperties } from '@/context/properties-context';
import { getGeocode } from '@/lib/get-geo-location';
import { useState, useCallback } from 'react';

export interface LocationResult {
	latitude: number | null;
	longitude: number | null;
	name: string | null;
	error: string | null;
	loading: boolean;
}

const useGeoLocationAddress = (): [
	LocationResult,
	() => Promise<LocationResult>
] => {
	const [locationResult, setLocationResult] = useState<LocationResult>({
		latitude: null,
		longitude: null,
		name: null,
		error: null,
		loading: false,
	});

	const { updateProperty } = useProperties();

	const getLocationAndAddress =
		useCallback(async (): Promise<LocationResult> => {
			if (!navigator.geolocation) {
				const result = {
					latitude: 0,
					longitude: 0,
					name: '',
					error: 'Geolocation is not supported by this browser.',
					loading: false,
				};
				setLocationResult(result);
				return result;
			}

			setLocationResult((prev) => ({ ...prev, loading: true, error: null }));

			return new Promise<LocationResult>((resolve) => {
				navigator.geolocation.getCurrentPosition(
					async (position) => {
						const { latitude, longitude } = position.coords;

						try {
							const data = await getGeocode(latitude, longitude);
							let name: string | null = null;

							if (data.results?.length) {
								const { formatted, components } = data.results[0];
								name = components.village
									? `${components.village}${
											components.country ? `, ${components.country}` : ''
									  }`
									: components.state
									? `${components.state}${
											components.country ? `, ${components.country}` : ''
									  }`
									: formatted || null;
							}

							if (!name) name = 'Address Not Found';

							const result: LocationResult = {
								latitude,
								longitude,
								name: name ?? '',
								error: null,
								loading: false,
							};

							setLocationResult(result);
							updateProperty('location', {
								name: name ?? '',
								lat: latitude,
								lng: longitude,
							});
							updateProperty('sortBy', 'distance');
							resolve(result);
						} catch (err) {
							const result: LocationResult = {
								latitude,
								longitude,
								name: '',
								error: 'Failed to fetch address.',
								loading: false,
							};
							setLocationResult(result);
							console.error('Error fetching address:', err);
							resolve(result);
						}
					},
					(error) => {
						let errorMessage = 'Failed to get location.';
						switch (error.code) {
							case error.PERMISSION_DENIED:
								errorMessage = 'User denied the request for Geolocation.';
								break;
							case error.POSITION_UNAVAILABLE:
								errorMessage = 'Location information is unavailable.';
								break;
							case error.TIMEOUT:
								errorMessage = 'The request to get user location timed out.';
								break;
						}

						const result: LocationResult = {
							latitude: 0,
							longitude: 0,
							name: '',
							error: errorMessage,
							loading: false,
						};

						setLocationResult(result);
						resolve(result);
					}
				);
			});
		}, [updateProperty]);

	return [locationResult, getLocationAndAddress];
};

export default useGeoLocationAddress;
